import * as d3 from 'd3'
import * as dc from 'dc'
import { HOUR_LABELS } from '@/utils/flowSightHourBuckets'
import { BUY_COLOR, SELL_COLOR, NEUTRAL_COLOR } from '@/utils/flowSightColors'
import { formatCompactAmount } from '@/utils/utils'
import { conversionRateOf, convertedVolumeOf, observationsOf } from '../calculations/conversionRate.js'
import { getChartTooltip, escapeHtml } from '../runtime/chartTooltip.js'

export function createConversionBubbleChart ({ el, chartGroup, width, height, dims, grp, t, drilldown }) {

  const keyToIndex = (k) => {
    const idx = parseInt(String(k).split('|')[0], 10)
    return Number.isFinite(idx) ? idx : 0
  }
  const sideOf = (k) => (String(k).endsWith('|BUY') ? 'BUY' : 'SELL')
  // Y and radius both come straight off the shared conversion reducer
  // (calculations/conversionRate.js): the rate is volDeal/vol and the radius is
  // volDeal itself, so the bubble cannot disagree with chart1 or the heatmap.
  const rateOf = conversionRateOf
  const cntOf = observationsOf
  const notionalOf = convertedVolumeOf

  const baseGroup = grp.grpConvIdxBubble

  // Radius domain is measured once, from the UNFILTERED data. Keeps a bubble's
  // size comparable before and after a drilldown, instead of the whole cloud
  // rescaling on every click.
  //
  // The domain MUST start at 0, not at the smallest notional. Anchoring it at
  // the minimum means the smallest bubble always lands exactly on dc's
  // MIN_RADIUS (10) - and dc only draws a label when `r > minRadiusWithLabel`,
  // which also defaults to 10. Strictly greater: the smallest bubble was
  // therefore ALWAYS unlabelled, and with a side filter on (one bubble left,
  // sitting at the domain floor) the chart lost its notional readout entirely.
  // Starting at 0 also makes the radius encode magnitude instead of rank, so
  // two notionals 7% apart stop rendering as a 10px dot next to a 100px disc.
  const notionals = baseGroup.all().map((d) => notionalOf(d.value))
  const maxNotional = Math.max(1, ...notionals)

  const bubble = dc.bubbleChart(el, chartGroup)
  bubble
    .width(width)
    .height(height + 40)
    // margem esquerda um pouco maior para respiro do eixo Y
    .margins({ top: 16, right: 24, bottom: 54, left: 68 })
    .dimension(dims.dimTenorIdxSideBubble) // chave real: "i|SIDE"
    .group(baseGroup)
    .controlsUseVisibility(true)
    .keyAccessor((d) => keyToIndex(d.key))
    .valueAccessor((d) => rateOf(d.value))
    .radiusValueAccessor((d) => Math.max(0, notionalOf(d.value)))
    // No .range() here on purpose: dc's plotData() overwrites it on every draw
    // with [MIN_RADIUS, xAxisLength * maxBubbleRelativeSize], so any range set
    // at construction is dead code. maxBubbleRelativeSize is the real knob.
    .r(d3.scaleSqrt().domain([0, maxNotional]))
    // Trimmed from 0.12: with the domain anchored at 0, mid-sized notionals now
    // render near the top of the range too, so the cap has to leave room for
    // neighbouring bubbles instead of assuming only one disc gets big.
    .maxBubbleRelativeSize(0.09)
    // Fixed X domain: one slot per maturity band, always all of them, like
    // chart1, the split chart and the heatmap on this tab. With elasticX the
    // domain collapsed onto whatever bands happened to hold data, and a linear
    // axis over a fractional range rendered the SAME label on every tick -
    // which is how the chart ended up showing "1-30D" eight times.
    //
    // The half-slot of padding at each end keeps an edge bubble from being cut
    // in half by the chart body: half a slot is 1/10 of the axis and the
    // largest radius is maxBubbleRelativeSize (0.09) of it.
    .x(d3.scaleLinear().domain([-0.5, HOUR_LABELS.length - 0.5]))
    .xUnits(dc.units.fp.precision(1))
    .y(d3.scaleLinear().domain([0, 1]))
    .elasticX(false)
    .elasticY(true)
    .yAxisPadding(0.08)
    .clipPadding(0)
    .renderHorizontalGridLines(true)
    .renderVerticalGridLines(false)
    .ordering((d) => -notionalOf(d.value)) // desenha grandes primeiro; pequenas por cima
    .brushOn(false)
    .colors(d3.scaleOrdinal()
      .domain(['BUY', 'SELL', 'NEUTRAL'])
      .range([BUY_COLOR, SELL_COLOR, NEUTRAL_COLOR]))
    .colorAccessor((d) => {
      if (drilldown.hasSelections()) {
        return drilldown.matchesDimensionKey(String(d.key)) ? sideOf(d.key) : 'NEUTRAL'
      }
      return sideOf(d.key)
    })
    // The SVG <title> dc would render here is replaced by the immediate tooltip
    // wired in decorate(): a 1s dwell is too slow for the only readable source
    // of this chart's X value (the axis repeats one tick label whenever the
    // data sits in a single maturity band).
    .renderTitle(false)
    .renderLabel(true)
    // dc writes a single line here; decorate() splits it into two tspans (rate
    // over volume) once the bubble is wide enough to hold both.
    .label((d) => (notionalOf(d.value) ? formatCompactAmount(notionalOf(d.value)) : ''))

  // One tick per band, pinned to the integer slots - without tickValues d3
  // picks its own (0.5, 1.5, ...) and every label repeats after rounding.
  bubble.xAxis()
    .tickValues(d3.range(HOUR_LABELS.length))
    .tickFormat((i) => HOUR_LABELS[i] || '')
  bubble.yAxis().ticks(6).tickFormat(d3.format('.0%'))

  // (event, datum) - NOT the (datum, index) of the root d3 5.16 in this repo.
  // These handlers are attached through chart.selectAll(), which is dc's OWN
  // nested d3-selection 2.0.0, and 2.x calls listeners the v6 way:
  // `listener.call(this, event, this.__data__)`. Getting this backwards reads
  // the event as the datum and silently toggles the key "undefined".
  const onClickNode = (event, d) => {
    event?.preventDefault?.()
    event?.stopPropagation?.()

    const key = String(d.key)
    drilldown.toggle('bubble', key)
  }

  // dc's default .filterAll()/.filters() only know about the chart's own
  bubble.filters = () => drilldown.values('bubble')
  bubble.filterAll = () => {
    drilldown.clear('bubble', { redraw: false })
    return bubble
  }
  // Lightweight clear (no recompute/redraw) for bar1/bar3's click handlers
  // to call BEFORE their own recompute - an external drilldown must not
  // leave this chart showing stale NEUTRAL bubbles from an old selection.
  bubble.clearSelectionSets = () => {
    drilldown.clear('bubble', { redraw: false })
  }
  bubble.selectedTenorIdxs = () => drilldown.values('bubble').map(keyToIndex)

  const getBubbleFill = (d) => {
    const key = String(d.key)
    if (drilldown.hasSelections()) {
      return drilldown.matchesDimensionKey(key) ? (sideOf(key) === 'BUY' ? BUY_COLOR : SELL_COLOR) : NEUTRAL_COLOR
    }
    return sideOf(key) === 'BUY' ? BUY_COLOR : SELL_COLOR
  }

  const tooltip = getChartTooltip()

  // Leads with the two axis readings, since that is what the axes themselves
  // cannot always show: X is the maturity band, Y the conversion rate. The
  // radius (converted volume) and the observation count follow.
  const tooltipHtml = (d) => {
    const tenor = HOUR_LABELS[keyToIndex(d.key)] || ''
    const side = sideOf(d.key)
    const row = (label, value) =>
      `<div><span style="color:var(--muted,#9aa3af)">${escapeHtml(label)}:</span> ${escapeHtml(value)}</div>`
    const sideColor = side === 'BUY' ? BUY_COLOR : SELL_COLOR
    return [
      `<div style="font-weight:700;color:${sideColor};margin-bottom:4px">${escapeHtml(side)}</div>`,
      row(`${t('flowSightSpot.hourOfDay')} (X)`, tenor),
      row(`${t('flowSightConversions.conversion')} (Y)`, `${(rateOf(d.value) * 100).toFixed(2)}%`),
      row(t('flowSightConversions.convertedVolume'), `R$ ${formatCompactAmount(notionalOf(d.value))}`),
      row(t('flowSightConversions.obs'), cntOf(d.value)),
    ].join('')
  }

  const decorate = (chart) => {
    const nodes = chart.selectAll('g.node, g.bubble')
    const circles = chart.selectAll('circle.bubble')

    // IMPORTANT: REMOVE DC NATIVE CLICK, USE ONLY OUR MANUAL CLICK
    nodes
      .on('click', null)
      .on('click.bubbles', onClickNode)

    // IMPORTANT: FORCE VISUAL STATE FROM OUR MANUAL SET
    drilldown.applySelectionClasses(nodes, (d) => d.key)

    // IMPORTANT: FORCE FILL + OPACITY AFTER DC REDRAW
    drilldown.applyTransition(circles)
    circles
      .attr('fill', (d) => getBubbleFill(d))
      .style('fill', (d) => getBubbleFill(d))
      .style('opacity', 1)
      // (event, datum), per dc's nested d3-selection 2.0.0 - see onClickNode.
      // `d3.event` is useless here: that global belongs to the ROOT d3 5.16,
      // a different module instance than the one dispatching this event, so it
      // reads null and blows up on .clientX.
      .on('mouseover.bubbles', function (event, d) {
        d3.select(this).style('opacity', 0.85)
        tooltip.show(tooltipHtml(d), event)
      })
      .on('mousemove.bubbles', (event) => tooltip.move(event))
      .on('mouseout.bubbles', function () {
        d3.select(this).style('opacity', 1)
        tooltip.hide()
      })

    // Two lines inside the bubble: the conversion rate (which the Y position
    // also encodes, but is unreadable off a compressed axis) over the converted
    // volume (which the radius encodes, but radius is only comparative).
    //
    // Only when the bubble can hold them - below MIN_TWO_LINE_RADIUS the second
    // line would spill outside the circle, so the volume alone stays.
    // Every bubble says the SAME two things - conversion rate and converted
    // volume. Only the placement adapts: both inside when the circle can hold
    // them, otherwise the rate stays centred and the volume moves just below
    // the circle, onto the empty background. Dropping the rate on small bubbles
    // (the earlier behaviour) made them look like a different chart.
    const MIN_TWO_LINE_RADIUS = 24
    chart.selectAll('g.node, g.bubble').each(function (d) {
      const node = d3.select(this)
      // Rebuilt every decorate, so redraws never stack copies.
      node.selectAll('text.bubble-extra').remove()

      const label = node.select('text')
      if (label.empty()) return

      label.attr('style', 'fill:#fff !important; font-weight:600; pointer-events:none')
      label.selectAll('tspan').remove()

      const volume = notionalOf(d.value)
      if (!volume) { label.text(''); return }

      const rateText = `${(rateOf(d.value) * 100).toFixed(1)}%`
      const volumeText = formatCompactAmount(volume)
      const radius = bubble.bubbleR(d)

      if (radius >= MIN_TWO_LINE_RADIUS) {
        label.text(null).attr('dy', 0)
        label.append('tspan')
          .attr('x', 0).attr('dy', '-0.1em')
          .text(rateText)
        label.append('tspan')
          .attr('x', 0).attr('dy', '1.2em')
          .attr('style', 'font-size:.82em; font-weight:400; fill-opacity:.85')
          .text(volumeText)
        return
      }

      // Coordinates here are local to g.node, which dc translates to the
      // bubble's centre - so y = radius + padding lands just under the edge.
      label.attr('dy', '.3em').text(rateText)
      node.append('text')
        .attr('class', 'bubble-extra')
        .attr('text-anchor', 'middle')
        .attr('x', 0)
        .attr('y', radius + 12)
        .text(volumeText)
        .attr('style', 'fill:#fff !important; font-size:10px; font-weight:400; fill-opacity:.85; pointer-events:none')
    })

    chart.selectAll('g.node, g.bubble')
      .sort((a, b) => d3.descending(notionalOf(a?.value), notionalOf(b?.value)))
  }

  // A redraw can delete the very bubble under the cursor (a drilldown may take
  // its volume to zero), and no mouseout fires for a node that no longer
  // exists - hide first, the next mouseover puts it back.
  bubble.on('preRedraw.bubbles', () => tooltip.hide())
  bubble.on('postRender.bubbles', decorate)
  bubble.on('postRedraw.bubbles', decorate)

  return bubble
}
